import { useEffect, useRef, useState } from 'react';
import { EmptyState } from '../../ui';
import { useMapProvider } from './MapProviderContext';
import type { MapController, MapMarker, MapView } from './MapProvider';

/** Props for {@link EventsMap}. */
export interface EventsMapProps {
  /** The pins to show. */
  readonly markers: readonly MapMarker[];
  /** Where the map looks. */
  readonly view: MapView;
  /** Called with a marker's id when tapped. */
  readonly onMarkerClick?: (id: string) => void;
  /** Height of the map surface. */
  readonly height?: number;
}

/**
 * Renders events as pins on a provider-backed map.
 *
 * Tile failures and a missing provider key are treated as a first-class state rather than an error:
 * the map area shows a short explanation and the surrounding screen keeps the list, so discovery
 * still works when the map does not. The provider comes from context, which is the swap point the
 * brief requires.
 *
 * @param props - Component props.
 * @returns The map element.
 */
export function EventsMap({
  markers,
  view,
  onMarkerClick,
  height = 420,
}: EventsMapProps): React.JSX.Element {
  const provider = useMapProvider();
  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<MapController | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  // Latest callback without re-creating the map when the parent re-renders.
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;

    if (container === null) {
      return;
    }

    void (async () => {
      try {
        const controller = await provider.create(container, {
          view,
          onMarkerClick: (id) => onMarkerClickRef.current?.(id),
        });

        if (cancelled) {
          controller?.destroy();
          return;
        }

        if (controller === null) {
          setUnavailable(true);
          return;
        }

        controllerRef.current = controller;
        controller.setMarkers(markers);
      } catch {
        // A tile or SDK failure degrades to the list rather than taking the screen down.
        if (!cancelled) {
          setUnavailable(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [provider]);

  useEffect(() => {
    controllerRef.current?.setMarkers(markers);
  }, [markers]);

  useEffect(() => {
    controllerRef.current?.setView(view);
  }, [view]);

  if (unavailable) {
    return (
      <div className="gm-map gm-map--unavailable" style={{ minHeight: height }}>
        <EmptyState
          title="Map unavailable"
          description="The map could not load. The list below still shows every event in this area."
        />
      </div>
    );
  }

  return <div ref={containerRef} className="gm-map" style={{ height }} aria-label="Map of events" />;
}
